/**
 * 게시물 «클라이언트» 정렬.
 *
 * ⚠️ `SORT_TYPES` 가 리포에 **셋**이다. 「합치지 않는다」가 결론이다:
 *
 *  · 여기(클라 정렬)              LATEST · POPULAR · COMMENT · LIKE · VIEW · ER  — 여섯
 *  · `moments/els/moment-query`   위 여섯 + **RELEVANCE**(BM25 `_score` 우선) · `as const`
 *  · `components/cmui/post-card`  LATEST · POPULAR 둘뿐 — 배럴로만 재수출되고 소비처는 없다
 *
 *  여섯 키의 «값»은 글자까지 같다. 그런데 `RELEVANCE` 는 **ES 정렬에만 있는 값**이고 아래
 *  `sortPosts` 는 그것을 처리하지 못한다(default 로 떨어져 최신순이 된다) — 같은 상수가 아니다.
 *  그리고 여기서 `moment-query` 를 재수출하면 그 모듈(449줄)과 그것이 끄는
 *  `utils/query-transform`(678줄)이 이 파일을 읽는 곳 전부의 번들 그래프에 붙는다.
 *  둘을 오가는 «매핑»은 옆 파일 `moment-feed-sort-utils.ts` 가 이미 맡고 있다.
 *
 * ⚠️ `sortPostsForFeed`(`moment-feed-sort-utils`)와도 다르다 — 그쪽 POPULAR 는
 *  `useMetricScoreSort` 를 호출자에게 받고 동점을 `postDate`/`timestamp` 로 한 번 더 가른다.
 *  아래 `sortByPopularity` 는 `false` 고정에 동점 처리가 없다. 합치면 카드 수치와 정렬 순서가 어긋나는 회귀다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

import { toEpochMs } from 'src/utils/moments/domain/post-date-coerce';
import { getPopularityScore } from 'src/utils/metrics/popularity-score';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { getEngagementRatePercentForSort } from 'src/utils/metrics/metric-reaction-stats';

/**
 * 게시물 배열을 최신순으로 정렬
 * @param {Array} posts - 게시물 배열
 * @returns {Array} 최신순으로 정렬된 게시물 배열
 * 
 * @example
 * sortByLatest([{ postDate: 20250115 }, { postDate: 20250110 }]) // 최신순 정렬
 */
export const sortByLatest = (posts: SocialPostInput[] | null | undefined): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];

  return [...posts].sort((a, b) => {
    // 1. postDate 비교 (숫자형 YYYYMMDD)
    const aPostDate = a.postDate;
    const bPostDate = b.postDate;

    if (aPostDate && bPostDate) {
      // 둘 다 postDate가 있는 경우
      // 원본은 JS 암묵 형변환에 기댔다 (postDate는 숫자·문자열 둘 다 온다)
      const dateDiff = Number(bPostDate) - Number(aPostDate);
      if (dateDiff !== 0) return dateDiff;
    } else if (aPostDate && !bPostDate) {
      return -1; // a가 postDate가 있으면 우선
    } else if (!aPostDate && bPostDate) {
      return 1; // b가 postDate가 있으면 우선
    }

    // 2. timestamp 비교
    const aTimestamp = a.timestamp;
    const bTimestamp = b.timestamp;

    if (aTimestamp && bTimestamp) {
      // 둘 다 timestamp가 있는 경우
      const aTime = typeof aTimestamp === 'number' ? aTimestamp * 1000 : new Date(aTimestamp).getTime();
      const bTime = typeof bTimestamp === 'number' ? bTimestamp * 1000 : new Date(bTimestamp).getTime();
      const timeDiff = bTime - aTime;
      if (timeDiff !== 0) return timeDiff;
    } else if (aTimestamp && !bTimestamp) {
      return -1; // a가 timestamp가 있으면 우선
    } else if (!aTimestamp && bTimestamp) {
      return 1; // b가 timestamp가 있으면 우선
    }

    // 3. createdAt 비교
    const aCreatedAt = a.createdAt;
    const bCreatedAt = b.createdAt;

    if (aCreatedAt && bCreatedAt) {
      const aCreatedTime = toEpochMs(aCreatedAt);
      const bCreatedTime = toEpochMs(bCreatedAt);
      const createdDiff = bCreatedTime - aCreatedTime;
      if (createdDiff !== 0) return createdDiff;
    } else if (aCreatedAt && !bCreatedAt) {
      return -1; // a가 createdAt이 있으면 우선
    } else if (!aCreatedAt && bCreatedAt) {
      return 1; // b가 createdAt이 있으면 우선
    }

    // 모든 필드가 없으면 원래 순서 유지 (안정 정렬)
    return 0;
  });
};

/**
 * 게시물 배열을 인기순으로 정렬
 * @param {Array} posts - 게시물 배열
 * @returns {Array} 인기순으로 정렬된 게시물 배열
 * 
 * @example
 * sortByPopularity([{ metricScore: 85 }, { metricScore: 90 }]) // 인기순 정렬
 */
export const sortByPopularity = (
  posts: SocialPostInput[] | null | undefined,
  options: { useMetricScoreSort?: boolean } = {},
): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];
  const { useMetricScoreSort = false } = options;

  return [...posts].sort((a, b) => {
    const scoreA = getPopularityScore(a, { useMetricScoreSort });
    const scoreB = getPopularityScore(b, { useMetricScoreSort });
    return scoreB - scoreA; // 인기순 (내림차순)
  });
};

/**
 * 댓글 수 기준으로 정렬
 * @param {Array} posts - 게시물 배열
 * @returns {Array} 정렬된 게시물 배열
 */
export const sortByComment = (posts: SocialPostInput[] | null | undefined): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];

  return [...posts].sort((a, b) => {
    const commentA = getMetricsByScoreTime(a)?.commentCount || 0;
    const commentB = getMetricsByScoreTime(b)?.commentCount || 0;
    return commentB - commentA; // 댓글 많은 순 (내림차순)
  });
};

/**
 * 좋아요 수 기준으로 정렬
 * @param {Array} posts - 게시물 배열
 * @returns {Array} 정렬된 게시물 배열
 */
export const sortByLike = (posts: SocialPostInput[] | null | undefined): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];

  return [...posts].sort((a, b) => {
    const likeA = getMetricsByScoreTime(a)?.likeCount || 0;
    const likeB = getMetricsByScoreTime(b)?.likeCount || 0;
    return likeB - likeA; // 좋아요 많은 순 (내림차순)
  });
};

/**
 * 조회수 기준으로 정렬
 * @param {Array} posts - 게시물 배열
 * @returns {Array} 정렬된 게시물 배열
 */
export const sortByView = (posts: SocialPostInput[] | null | undefined): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];

  return [...posts].sort((a, b) => {
    const viewA = getMetricsByScoreTime(a)?.viewCount || 0;
    const viewB = getMetricsByScoreTime(b)?.viewCount || 0;
    return viewB - viewA; // 조회수 많은 순 (내림차순)
  });
};

/**
 * 정렬 타입 상수
 */
export const SORT_TYPES = {
  LATEST: 'latest',
  POPULAR: 'popular',
  COMMENT: 'comment',
  LIKE: 'like',
  VIEW: 'view',
  ER: 'er',
};

/**
 * 게시물 배열을 지정된 기준으로 정렬
 * @param {Array} posts - 게시물 배열
 * @param {string} sortType - 정렬 타입 ('latest' | 'popular')
 * @returns {Array} 정렬된 게시물 배열
 * 
 * @example
 * sortPosts(posts, SORT_TYPES.LATEST) // 최신순 정렬
 * sortPosts(posts, SORT_TYPES.POPULAR) // 인기순 정렬
 */
export const sortPosts = (
  posts: SocialPostInput[] | null | undefined,
  sortType: string,
): SocialPostInput[] => {
  if (!Array.isArray(posts)) return [];

  switch (sortType) {
    case SORT_TYPES.LATEST:
      return sortByLatest(posts);
    case SORT_TYPES.POPULAR:
      return sortByPopularity(posts);
    case SORT_TYPES.COMMENT:
      return sortByComment(posts);
    case SORT_TYPES.LIKE:
      return sortByLike(posts);
    case SORT_TYPES.VIEW:
      return sortByView(posts);
    case SORT_TYPES.ER: {
      const withScore = posts.map((post) => ({
        post,
        er: getEngagementRatePercentForSort(post),
      }));
      withScore.sort((a, b) => {
        if (a.er === null && b.er === null) return 0;
        if (a.er === null) return 1;
        if (b.er === null) return -1;
        return b.er - a.er;
      });
      return withScore.map((it) => it.post);
    }
    default:
      return sortByLatest(posts); // 기본값은 최신순
  }
};
